import { clsx, type ClassValue } from 'clsx'
import { twMerge } from 'tailwind-merge'
import mimeTypes from 'mime-types'
import escapeRegExp from 'lodash/escapeRegExp'
import { AxiosError } from 'axios'
import { toast } from 'react-toastify'
import { JobDescription } from '@/interfaces/jobDescription'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Normalizes Vietnamese text by converting all characters to lowercase
 * and replacing accented characters with their base characters
 */
export function normalizeVietnameseText(text: string): string {
  return text
    .toLowerCase()
    .replace(/à|á|ạ|ả|ã|â|ầ|ấ|ậ|ẩ|ẫ|ă|ằ|ắ|ặ|ẳ|ẵ/g, 'a')
    .replace(/è|é|ẹ|ẻ|ẽ|ê|ề|ế|ệ|ể|ễ/g, 'e')
    .replace(/ì|í|ị|ỉ|ĩ/g, 'i')
    .replace(/ò|ó|ọ|ỏ|õ|ô|ồ|ố|ộ|ổ|ỗ|ơ|ờ|ớ|ợ|ở|ỡ/g, 'o')
    .replace(/ù|ú|ụ|ủ|ũ|ư|ừ|ứ|ự|ử|ữ/g, 'u')
    .replace(/ỳ|ý|ỵ|ỷ|ỹ/g, 'y')
    .replace(/đ/g, 'd')
}

export function capitalizeFirstLetter(word: string): string {
  return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
}

export function isHeadlineContainsHighlight(headline: string, highlight: string): boolean {
  const escapedHighlight = escapeRegExp(highlight)
  return new RegExp(`(${escapedHighlight})`, 'i').test(headline)
}

export function mimeTypeToExtension(mimeTypeArg: string): string {
  if (mimeTypeArg === 'system_sync') {
    return 'png'
  }
  const extension = mimeTypes.extension(mimeTypeArg)
  return extension || ''
}

/**
 * Calculates a darker stroke color from a given fill color.
 *
 * @param fillColor - The fill color in hex format (e.g., "#1c65bb").
 * @returns The calculated stroke color in hex format.
 */
export function getMatchingStrokeColorFromFill(fillColor: string): string {
  // 1. Parse the hex string into R, G, B components
  let r = 0,
    g = 0,
    b = 0
  if (fillColor.length === 4) {
    r = parseInt(fillColor[1] + fillColor[1], 16)
    g = parseInt(fillColor[2] + fillColor[2], 16)
    b = parseInt(fillColor[3] + fillColor[3], 16)
  } else if (fillColor.length === 7) {
    r = parseInt(fillColor.substring(1, 3), 16)
    g = parseInt(fillColor.substring(3, 5), 16)
    b = parseInt(fillColor.substring(5, 7), 16)
  }

  // Normalize R, G, B to be between 0 and 1
  const r_norm = r / 255
  const g_norm = g / 255
  const b_norm = b / 255

  // 2. Convert RGB to HSL
  const max = Math.max(r_norm, g_norm, b_norm)
  const min = Math.min(r_norm, g_norm, b_norm)
  let h = 0,
    s = 0,
    l = (max + min) / 2

  if (max !== min) {
    const d = max - min
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
    switch (max) {
      case r_norm:
        h = (g_norm - b_norm) / d + (g_norm < b_norm ? 6 : 0)
        break
      case g_norm:
        h = (b_norm - r_norm) / d + 2
        break
      case b_norm:
        h = (r_norm - g_norm) / d + 4
        break
    }
    h /= 6
  }

  // 3. Apply the transformation: reduce lightness by 38%
  l = l * (1 - 0.38)

  // 4. Convert the new HSL back to RGB
  let r_new = l,
    g_new = l,
    b_new = l
  if (s !== 0) {
    const hue2rgb = (p: number, q: number, t: number) => {
      if (t < 0) t += 1
      if (t > 1) t -= 1
      if (t < 1 / 6) return p + (q - p) * 6 * t
      if (t < 1 / 2) return q
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6
      return p
    }
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s
    const p = 2 * l - q
    r_new = hue2rgb(p, q, h + 1 / 3)
    g_new = hue2rgb(p, q, h)
    b_new = hue2rgb(p, q, h - 1 / 3)
  }

  // 5. Convert new RGB values to hex
  const toHex = (c: number) => {
    const hex = Math.round(c * 255).toString(16)
    return hex.length === 1 ? '0' + hex : hex
  }

  return `#${toHex(r_new)}${toHex(g_new)}${toHex(b_new)}`
}

export const getTemplateImageKeyFromUrl = (url: string) => {
  return url.split('templates/')?.[1]?.split('?')?.[0] ?? url // only catch the image name (getFile vs S3)
}

export const getFileNameWithoutExtension = (fileName: string) => {
  return fileName.split('.')?.[0] ?? fileName
}

export const formatFileSize = (bytes: number): string => {
  if (!bytes) return ''
  const units = ['bytes', 'KB', 'MB', 'GB']
  let size = bytes
  let unitIndex = 0

  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024
    unitIndex++
  }

  // For bytes, show as whole numbers, otherwise show with 2 decimal places
  return unitIndex === 0
    ? `${Math.round(size)} ${units[unitIndex]}`
    : `${size.toFixed(2)} ${units[unitIndex]}`
}

export const getErrorMessage = (error: unknown) => {
  if (error instanceof AxiosError) {
    return error.response?.data?.message ?? error.message
  }
  return error instanceof Error ? error.message : String(error)
}

/**
 * Handles error with optional toast notification.
 *
 * @param error - The error object
 * @param options - Configuration options
 * @param options.showToast - Whether to show a toast notification (default: true)
 * @param options.fallbackMessage - Optional fallback message to use instead of the extracted error message
 * @returns The extracted error message
 */
export const handleError = (
  error: unknown,
  options?: {
    showToast?: boolean
    fallbackMessage?: string
  }
) => {
  const { showToast = true, fallbackMessage } = options || {}
  const errorMessage = getErrorMessage(error)
  console.log(111, errorMessage)
  const displayMessage = errorMessage || fallbackMessage

  if (showToast) {
    toast.error(displayMessage)
  }

  return displayMessage
}

// Get formatted role text - simplified to concatenate userRole + role with styling
export const formatRoleText = (
  role: string,
  userRole?: string,
  isJDDetail: boolean = false,
  isTrending: boolean = false
): React.ReactNode => {
  // role no longer contains "kiêm"
  // userRole is the current user's role
  // Concatenate: userRole + "kiêm" + role with conditional styling
  if (!userRole) {
    return role || 'Untold JD'
  }

  if (!role) {
    return userRole
  }

  // For MyJD - normal font weight
  if (isJDDetail) {
    return (
      <>
        <span className="font-normal text-black">{userRole} kiêm</span>
        <span className="text-black"> {role}</span>
      </>
    )
  }

  // For Trending - bold but all black
  if (isTrending) {
    return (
      <>
        <p className="text-black">{userRole}</p>
        <p className="text-black"> kiêm {role}</p>
      </>
    )
  }

  // Default - bold with primary color for "kiêm {role}"
  return (
    <>
      <p className="font-medium text-black">{userRole}</p>
      <p className="text-primary font-medium"> kiêm {role}</p>
    </>
  )
}

// Legacy function - kept for backward compatibility
export const getRoleText = (
  jd: JobDescription,
  isJDDetail: boolean = false,
  isTrending: boolean = false
): React.ReactNode => {
  return formatRoleText(jd.role, jd.user?.role, isJDDetail, isTrending)
}

// Get contributors count text
export const getContributorText = (jd: JobDescription, showAll: boolean = false) => {
  const contributors = jd.contributors || []
  const contributorCount = contributors.length

  if (contributorCount === 0) {
    return null
  }

  // Decide how many contributors to display
  const displayContributors = showAll ? contributors : contributors.slice(0, 3)
  const remainingCount = showAll ? 0 : contributorCount - displayContributors.length

  // Build the text with contributor names, numbering anonymous contributors
  let anonymousCount = 0
  const totalAnonymous = contributors.filter((c) => c.name === 'Anonymous').length

  const displayNames = displayContributors.map((contributor) => {
    if (contributor.name === 'Anonymous') {
      anonymousCount++
      // Only add number if there's more than one anonymous contributor
      return totalAnonymous > 1 ? `Ẩn danh ${anonymousCount}` : 'Ẩn danh'
    }
    return contributor.name
  })
  const contributorsList = displayNames.join(', ')

  if (remainingCount > 0) {
    return `${contributorsList} và ${remainingCount}+ momoers khác vừa tạo thêm nội dung cho Untold JD này`
  }

  return `${contributorsList} vừa tạo thêm nội dung cho Untold JD này`
}

// Get reactions text - who reacted to the JD (grouped by reaction types present)
export const getReactionText = (jd: JobDescription, showAll: boolean = false) => {
  const reactions = jd.reactions || []

  if (reactions.length === 0) {
    return null
  }

  // Get unique users (one user can only have one reaction type)
  const uniqueUsers = new Map<string, { displayName: string; reactionType: 'heart' | 'haha' }>()

  reactions.forEach((r) => {
    if (!uniqueUsers.has(r.userId)) {
      uniqueUsers.set(r.userId, { displayName: r.displayName, reactionType: r.reactionType })
    }
  })

  // Check which reaction types are present
  const hasHeart = Array.from(uniqueUsers.values()).some((u) => u.reactionType === 'heart')
  const hasHaha = Array.from(uniqueUsers.values()).some((u) => u.reactionType === 'haha')

  // Determine icon display
  let iconType: 'both' | 'heart' | 'haha'
  if (hasHeart && hasHaha) {
    iconType = 'both'
  } else if (hasHeart) {
    iconType = 'heart'
  } else {
    iconType = 'haha'
  }

  // Get all user names
  const allNames = Array.from(uniqueUsers.values()).map((u) => u.displayName)

  const maxDisplay = showAll ? Infinity : 3
  const displayNames = allNames.slice(0, maxDisplay)
  const remaining = allNames.length - displayNames.length

  return {
    iconType,
    names: displayNames,
    remaining,
  }
}
